import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock, CheckCircle2, Play, Square, AlertCircle } from "lucide-react";

const PERIOD_TIME = {
    1: "08:00 – 08:45",
    2: "08:50 – 09:35",
    3: "09:40 – 10:25",
    4: "10:55 – 11:40",
    5: "11:45 – 12:30",
    6: "12:35 – 13:20",
    7: "13:25 – 14:10",
};

const elapsedMinutes = (iso) => {
    if (!iso) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
};

/** Compact "Today's Lessons" banner with check-in / check-out controls. */
export const TeacherTodayLessons = () => {
    const [data, setData] = useState({ lessons: [], date: "", weekday: -1 });
    const [busyId, setBusyId] = useState(null);
    const [tick, setTick] = useState(0);

    const refresh = async () => {
        try {
            const r = await api.get("/teacher/today-lessons");
            setData(r.data);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        refresh();
        const t = setInterval(() => setTick((x) => x + 1), 30000); // re-render timer every 30s
        return () => clearInterval(t);
    }, []);

    const checkIn = async (slot) => {
        setBusyId(slot.id);
        try {
            await api.post("/teacher/lesson/check-in", {
                class_id: slot.class_id, subject_id: slot.subject_id, period: slot.period,
            });
            refresh();
        } catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
        finally { setBusyId(null); }
    };

    const checkOut = async (lessonId) => {
        setBusyId(lessonId);
        try {
            await api.post(`/teacher/lesson/check-out/${lessonId}`);
            refresh();
        } catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
        finally { setBusyId(null); }
    };

    if (data.lessons.length === 0) return null;

    return (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-5" data-testid="today-lessons">
            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                <Clock className="w-4 h-4 text-oxford" />
                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Orët e mia sot</div>
                <span className="text-[10px] text-slate-500">{data.date} • {data.lessons.length} orë</span>
                <div className="ml-auto text-[10px] text-slate-500 hidden sm:block">Konfirmo që po jep mësim — koordinatori sheh në kohë reale</div>
            </div>
            <div className="divide-y divide-slate-200">
                {data.lessons.map((l) => {
                    const lesson = l.lesson;
                    const inProgress = lesson && !lesson.checked_out_at;
                    const completed = lesson && lesson.checked_out_at;
                    const timer = inProgress ? elapsedMinutes(lesson.checked_in_at) : null;
                    return (
                        <div key={`${l.id}`} className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`lesson-row-${l.period}`}>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-heading font-black text-sm shrink-0 ${completed ? "bg-sage/15 text-sage" : inProgress ? "bg-amber-100 text-amber-700" : "bg-oxford/10 text-oxford"}`}>
                                {l.period}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-bold text-slate-900 text-sm truncate">{l.subject_name}</span>
                                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-oxford/10 text-oxford font-bold">{l.class_name}</span>
                                    <span className="text-[10px] text-slate-500 font-mono">{PERIOD_TIME[l.period]}</span>
                                </div>
                                {lesson && (
                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                        {completed ? (
                                            <>✓ Përfundoi • {lesson.actual_minutes}min reale {lesson.late_minutes > 0 && <span className="text-amber-700">• vonesa {lesson.late_minutes}min</span>}</>
                                        ) : (
                                            <>• Po jep mësim — <strong className="text-amber-700">{timer}min të kaluara</strong> {lesson.late_minutes > 0 && <span>• hyri me vonesë {lesson.late_minutes}min</span>}</>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="shrink-0">
                                {!lesson && (
                                    <button onClick={() => checkIn(l)} disabled={busyId === l.id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-oxford hover:bg-oxfordhover text-white text-xs font-semibold disabled:opacity-50"
                                        data-testid={`lesson-checkin-${l.period}`}>
                                        <Play className="w-3 h-3" /> Hyr në klasë
                                    </button>
                                )}
                                {inProgress && (
                                    <button onClick={() => checkOut(lesson.id)} disabled={busyId === lesson.id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sage hover:bg-sage/90 text-white text-xs font-semibold disabled:opacity-50"
                                        data-testid={`lesson-checkout-${l.period}`}>
                                        <Square className="w-3 h-3" /> Mbaro orën
                                    </button>
                                )}
                                {completed && (
                                    <span className="inline-flex items-center gap-1 text-sage text-xs font-bold" data-testid={`lesson-done-${l.period}`}>
                                        <CheckCircle2 className="w-4 h-4" />
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
